import { useSelector, shallowEqual } from 'react-redux';
import { useJourneyData } from 'hooks/useJourneyData';
import { selectEmail } from 'state/email-capture/email-capture-slice';
import { useElecMultirate } from 'hooks/useElecMultirate';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { AbandonedCart, AbandonedCartStatus, Product } from 'state/abandoned-cart/types/abandoned-cart';
import { selectData } from 'state/abandoned-cart/abandoned-cart-slice';
import { selectFilteredProducts } from 'state/quote/quote-slice.ts';
import { routePath } from 'config/route-config';

const { VITE_PATH_PREFIX = '' } = process.env;

const returnStatuses = [
  AbandonedCartStatus.RETURNED,
  AbandonedCartStatus.RETURNINPROGRESS
]

export const useAbandonedCartInit = () : {
  isAbandonedCartAvailable: boolean;
  payload?: AbandonedCart;
} => {
  const { releaseAbandonedCart } = useFlags();

  const email = useSelector(selectEmail, shallowEqual);
  const abandonedCartData = useSelector(selectData, shallowEqual);
  const products = useSelector(selectFilteredProducts, shallowEqual);
  const { showE7Tariffs, showTOUTariffs } = useElecMultirate();
  const {
    consumption,
    address,
    mpan,
    mprn,
    paymentMethod,
    meterType,
    fuelTypeString
  } = useJourneyData();

  const hasProducts = products.length > 0;
  const isAbandonedCartAvailable =
    !!releaseAbandonedCart &&
    !!email &&
    hasProducts &&
    !returnStatuses.includes(abandonedCartData.status);

  if (!isAbandonedCartAvailable) {
    return {
      isAbandonedCartAvailable
    }
  }

  const { electricityTil, gasTil } = products[0];
  const tariffType =
    electricityTil?.tariffType || gasTil?.tariffType;

  const returnURL = `${window.location.origin}${VITE_PATH_PREFIX}${routePath.abandonedCart.replace('/:quoteId', '')}`;

  const payload = {
    status: abandonedCartData.status,
    channel: abandonedCartData.channel,
    journey: {
      journeyURL: returnURL,
      electricityConsumption: consumption.electric || undefined,
      gasConsumption: consumption.gas || undefined,
      MPAN: mpan || undefined,
      MPRN: mprn || undefined,
      postCode: address.postcode,
      meterType,
      isTOU: showTOUTariffs,
      isEconomy7: showE7Tariffs,
      fuel: fuelTypeString,
      paymentOption: paymentMethod,
      tariffType,
      currentAddress: {
        ...address,
        udprn: Number(address.udprn)
      },
    },
    products: [products[0]].map((products: Product) => ({
      code: products.code,
      name: products.name,
      annualAmount: products.annualAmount,
      monthlyAmount: products.monthlyAmount
    })),
    customerDetails: {
      emailAddress: email
    }
  }

  return {
    isAbandonedCartAvailable,
    payload
  }
};
